import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  GripVertical,
  User,
  Globe,
  Check,
} from 'lucide-react';
import {
  useUpdateCompanyConfiguration,
  useGetCompanyData,
} from '@/hooks/use-company';
import { useWebhookConfig, useCreateWebhookConfig } from '@/hooks/use-webhook';
import { useToast } from '@/hooks/use-toast';
import WebhookConnectionGuide from '@/components/settings/WebhookConnectionGuide';
import { WEBHOOK_PLATFORMS } from '@/components/settings/webhook-setup-content';
import { resolveCopyableWebhookUrl } from '@/components/settings/webhook-url';

// Email validation helper
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Form validation schema
const formSchema = z.object({
  groundPsi: z
    .string()
    .min(1, 'Ground PSI is required')
    .refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 500 && num <= 12000;
    }, 'Ground PSI must be between 500 and 12,000'),
  footingDepth: z
    .string()
    .min(1, 'Footing Depth is required')
    .refine((val) => {
      const num = Number(val);
      return !isNaN(num) && num >= 0 && num <= 100;
    }, 'Footing Depth must be between 0 and 100'),
  permitProcess: z.boolean(),
  leadSources: z.array(z.string()), // Made optional - no minimum requirement
  municipalities: z
    .array(z.string())
    .min(1, 'At least one municipality is required'),
  proposalNotifications: z.object({
    modified: z.array(z.string()),
    sold: z.array(z.string()),
  }),
});

type FormValues = z.infer<typeof formSchema>;

export default function Step3CompanyConfiguration() {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('orgId');

  // API mutation hook
  const { mutate: updateConfiguration, isPending } =
    useUpdateCompanyConfiguration();

  // Get company data for prefilling
  const { data: companyData, isLoading: isLoadingCompanyData } =
    useGetCompanyData(organizationId, 'configuration');

  // Webhook state
  const { toast } = useToast();
  const { data: webhookConfig } = useWebhookConfig();
  const createWebhookConfig = useCreateWebhookConfig();
  const [enableWebhook, setEnableWebhook] = useState(false);
  const [webhookPlatform, setWebhookPlatform] = useState('ELEMENTOR');
  const [webhookCreated, setWebhookCreated] = useState(false);
  const [webhookUrlWithSecret, setWebhookUrlWithSecret] = useState<string | null>(null);
  const copyableOnboardingUrl = resolveCopyableWebhookUrl(
    webhookUrlWithSecret ?? webhookConfig?.webhookUrl,
  );

  const handleCreateWebhook = () => {
    createWebhookConfig.mutate(
      { platform: webhookPlatform },
      {
        onSuccess: (data) => {
          setWebhookCreated(true);
          if (data.webhookUrl) {
            setWebhookUrlWithSecret(data.webhookUrl);
          }
          toast({ title: 'Webhook URL generated successfully' });
        },
      },
    );
  };

  const copyWebhookUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ title: 'Webhook URL copied to clipboard' });
  };

  // State for new items being added
  const [newLeadSource, setNewLeadSource] = useState('');
  const [newMunicipality, setNewMunicipality] = useState('');
  const [newProposalModifiedEmail, setNewProposalModifiedEmail] = useState('');
  const [newProposalSoldEmail, setNewProposalSoldEmail] = useState('');

  // State for email validation errors
  const [emailErrors, setEmailErrors] = useState({
    proposalModified: '',
    proposalSold: '',
  });

  // State for drag and drop
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [draggedType, setDraggedType] = useState<string | null>(null);

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      groundPsi: '500',
      footingDepth: '0',
      permitProcess: false,
      leadSources: [],
      municipalities: [],
      proposalNotifications: {
        modified: [],
        sold: [],
      },
    },
  });

  // Prefill form when company data is loaded
  useEffect(() => {
    if (companyData?.configuration) {
      const config = companyData.configuration;

      // Set form values with API data or defaults
      form.setValue('groundPsi', config.groundPsi || '500');
      form.setValue('footingDepth', config.footingDepth || '0');
      form.setValue('permitProcess', config.permitProcess || false);
      form.setValue('leadSources', config.leadSources || []);
      // Municipalities may be stored as objects ({ name, locations }) by Settings;
      // onboarding only manages the names, so normalize to a string list here.
      form.setValue(
        'municipalities',
        (config.municipalities || [])
          .map(m => (typeof m === 'string' ? m : m?.name ?? ''))
          .filter(Boolean),
      );
      form.setValue('proposalNotifications', {
        modified: config.proposalNotifications?.modified || [],
        sold: config.proposalNotifications?.sold || [],
      });
    }
  }, [companyData, form]);

  const handleNext = async (data: FormValues) => {
    console.log('Step 3 data:', data);

    if (!organizationId) {
      console.error('No organization ID found');
      return false; // Prevent navigation
    }

    return new Promise((resolve, reject) => {
      // API call to update company configuration
      updateConfiguration(
        {
          companyId: organizationId,
          data: {
            groundPsi: data.groundPsi,
            footingDepth: data.footingDepth,
            permitProcess: data.permitProcess,
            leadSources: data.leadSources,
            municipalities: data.municipalities,
            proposalNotifications: {
              modified: data.proposalNotifications.modified,
              sold: data.proposalNotifications.sold,
            },
          },
        },
        {
          onSuccess: (response) => {
            console.log('Configuration saved successfully:', response);
            resolve(response);
            return true;
          },
          onError: (error) => {
            console.error('Failed to save configuration:', error);
            reject(error);
            return false; // Prevent navigation
          },
        }
      );
    });
  };

  // Helper functions for array manipulation
  const addLeadSource = () => {
    if (newLeadSource.trim()) {
      const currentSources = form.getValues('leadSources');
      form.setValue('leadSources', [...currentSources, newLeadSource.trim()]);
      setNewLeadSource('');
    }
  };

  const removeLeadSource = (index: number) => {
    const currentSources = form.getValues('leadSources');
    form.setValue(
      'leadSources',
      currentSources.filter((_, i) => i !== index)
    );
  };

  const addMunicipality = () => {
    if (newMunicipality.trim()) {
      const currentMunicipalities = form.getValues('municipalities');
      form.setValue('municipalities', [
        ...currentMunicipalities,
        newMunicipality.trim(),
      ]);
      setNewMunicipality('');
      // Clear any validation errors for this field
      form.clearErrors('municipalities');
    }
  };

  const removeMunicipality = (index: number) => {
    const currentMunicipalities = form.getValues('municipalities');
    const newMunicipalities = currentMunicipalities.filter(
      (_, i) => i !== index
    );
    form.setValue('municipalities', newMunicipalities);

    // Trigger validation if the array becomes empty
    if (newMunicipalities.length === 0) {
      form.trigger('municipalities');
    }
  };

  const addProposalModifiedEmail = () => {
    if (newProposalModifiedEmail.trim()) {
      if (!isValidEmail(newProposalModifiedEmail.trim())) {
        setEmailErrors((prev) => ({
          ...prev,
          proposalModified: 'Please enter a valid email address',
        }));
        return;
      }
      const currentEmails = form.getValues('proposalNotifications.modified');
      form.setValue('proposalNotifications.modified', [
        ...currentEmails,
        newProposalModifiedEmail.trim(),
      ]);
      setNewProposalModifiedEmail('');
      setEmailErrors((prev) => ({ ...prev, proposalModified: '' }));
    }
  };

  const removeProposalModifiedEmail = (index: number) => {
    const currentEmails = form.getValues('proposalNotifications.modified');
    form.setValue(
      'proposalNotifications.modified',
      currentEmails.filter((_, i) => i !== index)
    );
  };

  const addProposalSoldEmail = () => {
    if (newProposalSoldEmail.trim()) {
      if (!isValidEmail(newProposalSoldEmail.trim())) {
        setEmailErrors((prev) => ({
          ...prev,
          proposalSold: 'Please enter a valid email address',
        }));
        return;
      }
      const currentEmails = form.getValues('proposalNotifications.sold');
      form.setValue('proposalNotifications.sold', [
        ...currentEmails,
        newProposalSoldEmail.trim(),
      ]);
      setNewProposalSoldEmail('');
      setEmailErrors((prev) => ({ ...prev, proposalSold: '' }));
    }
  };

  const removeProposalSoldEmail = (index: number) => {
    const currentEmails = form.getValues('proposalNotifications.sold');
    form.setValue(
      'proposalNotifications.sold',
      currentEmails.filter((_, i) => i !== index)
    );
  };

  // Drag and drop handlers
  const handleDragStart = (index: number, type: string) => {
    setDraggedIndex(index);
    setDraggedType(type);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (
    e: React.DragEvent,
    targetIndex: number,
    type: string
  ) => {
    e.preventDefault();

    if (draggedIndex === null || draggedType !== type) return;

    const fieldName = type === 'leadSources' ? 'leadSources' : 'municipalities';

    const currentItems = form.getValues(
      fieldName as keyof FormValues
    ) as string[];
    const newItems = [...currentItems];
    const draggedItem = newItems[draggedIndex];

    // Remove the dragged item
    newItems.splice(draggedIndex, 1);
    // Insert it at the new position
    newItems.splice(targetIndex, 0, draggedItem);

    form.setValue(fieldName as keyof FormValues, newItems as any);
    setDraggedIndex(null);
    setDraggedType(null);
  };

  // Clear email errors when user starts typing
  const handleEmailChange = (
    value: string,
    type: 'proposalModified' | 'proposalSold'
  ) => {
    if (type === 'proposalModified') {
      setNewProposalModifiedEmail(value);
      if (emailErrors.proposalModified) {
        setEmailErrors((prev) => ({ ...prev, proposalModified: '' }));
      }
    } else {
      setNewProposalSoldEmail(value);
      if (emailErrors.proposalSold) {
        setEmailErrors((prev) => ({ ...prev, proposalSold: '' }));
      }
    }
  };

  // Watch form values for real-time updates
  const watchedLeadSources = form.watch('leadSources');
  const watchedMunicipalities = form.watch('municipalities');
  const watchedProposalModifiedEmails = form.watch(
    'proposalNotifications.modified'
  );
  const watchedProposalSoldEmails = form.watch('proposalNotifications.sold');

  return (
    <OnboardingLayout
      currentStep={3}
      totalSteps={5}
      onNext={async () => {
        const isValid = await form.trigger();
        if (!isValid) return false;

        // Wrap the form submission to ensure it always returns a boolean
        try {
          // Create a promise that resolves to the result of handleNext
          return new Promise((resolve) => {
            form.handleSubmit(async (data) => {
              const result = await handleNext(data);
              resolve(result !== false); // If handleNext returns false, return false; otherwise true
            })();
          });
        } catch (error) {
          console.error('Form submission error:', error);
          return false;
        }
      }}
      isLoading={isPending}
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
            Company Configuration
          </h2>
          <p className="text-muted-foreground">
            Define key company-wide settings such as construction specs, permit
            workflows, lead sources, service areas, and proposal notification
            preferences.
          </p>
        </div>

        {isLoadingCompanyData ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-border-subtle"></div>
          </div>
        ) : (
          <Form {...form}>
            <div className="space-y-6">
              {/* Top Row - Construction Specifications and Permit Process */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 1. Construction Specifications */}
                <Card className="border border-border">
                  <CardHeader>
                    <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">
                      Construction Specifications
                      <span className="text-red-600">*</span>
                    </h3>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Ground PSI */}
                      <FormField
                        control={form.control}
                        name="groundPsi"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground font-medium">
                              Ground PSI<span className="text-red-600">*</span>
                            </FormLabel>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select Ground PSI" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {[500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000, 7000, 8000, 9000, 10000, 12000].map((psi) => (
                                  <SelectItem key={psi} value={String(psi)}>{psi.toLocaleString()} PSI</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Footing Depth */}
                      <FormField
                        control={form.control}
                        name="footingDepth"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground font-medium">
                              Footing Depth
                              <span className="text-red-600">*</span>
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  type="number"
                                  placeholder="Enter depth"
                                  min="0"
                                  max="100"
                                  step="1"
                                  {...field}
                                  className="pr-16"
                                />
                                {/* <div className="absolute right-12 top-1/2 transform -translate-y-1/2 text-sm text-muted-foreground">
                                  ft
                                </div> */}
                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex flex-col">
                                  <Button type="button"
                                    onClick={() => {
                                      const currentValue = Number(
                                        field.value || 0
                                      );
                                      const newValue = Math.min(
                                        100,
                                        currentValue + 1
                                      );
                                      field.onChange(String(newValue));
                                    }}
                                    className="p-1 hover:bg-muted rounded"
                                  >
                                    <ChevronUp className="w-3 h-3 text-muted-foreground" />
                                  </Button>
                                  <Button type="button"
                                    onClick={() => {
                                      const currentValue = Number(
                                        field.value || 0
                                      );
                                      const newValue = Math.max(
                                        0,
                                        currentValue - 1
                                      );
                                      field.onChange(String(newValue));
                                    }}
                                    className="p-1 hover:bg-muted rounded"
                                  >
                                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                                  </Button>
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Permit Process */}
                <Card className="border border-border">
                  <CardHeader>
                    <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">
                      LarkPM Services<span className="text-red-600">*</span>
                    </h3>
                  </CardHeader>
                  <CardContent>
                    <FormField
                      control={form.control}
                      name="permitProcess"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-xl border p-4">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-0.5 flex-1 ml-4">
                            <FormLabel className="text-sm font-medium">
                              Waiting period after contract signing before
                              architect drawings request
                            </FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Middle Row - Lead Sources and Municipalities */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 3. Lead Source Page */}
                <Card className="border border-border">
                  <CardHeader>
                    <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">
                      Lead Source List
                    </h3>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Lead Sources List */}
                    <FormField
                      control={form.control}
                      name="leadSources"
                      render={() => (
                        <FormItem>
                          <FormControl>
                            <div className="space-y-2">
                              {watchedLeadSources.map((source, index) => (
                                <div
                                  key={index}
                                  draggable
                                  onDragStart={() =>
                                    handleDragStart(index, 'leadSources')
                                  }
                                  onDragOver={handleDragOver}
                                  onDrop={(e) =>
                                    handleDrop(e, index, 'leadSources')
                                  }
                                  className={`flex items-center gap-3 p-3 bg-muted rounded-xl border cursor-move hover:bg-muted transition-colors ${draggedIndex === index &&
                                      draggedType === 'leadSources'
                                      ? 'opacity-50'
                                      : ''
                                    }`}
                                >
                                  <GripVertical className="w-4 h-4 text-muted-foreground/70" />
                                  <span className="text-sm font-medium text-foreground flex-1">
                                    {source}
                                  </span>
                                  <Button type="button"
                                    onClick={() => removeLeadSource(index)}
                                    className="p-1 hover:bg-muted rounded text-red-500"
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Add New Lead Source */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add new lead source"
                        value={newLeadSource}
                        onChange={(e) => setNewLeadSource(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === 'Enter' &&
                          (e.preventDefault(), addLeadSource())
                        }
                      />
                      <Button
                        type="button"
                        onClick={addLeadSource}
                        size="sm"
                        className="bg-foreground-soft hover:bg-foreground"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* 4. Municipalities List */}
                <Card className="border border-border">
                  <CardHeader>
                    <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">
                      Municipalities List<span className="text-red-600">*</span>
                    </h3>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Municipalities List */}
                    <FormField
                      control={form.control}
                      name="municipalities"
                      render={() => (
                        <FormItem>
                          <FormControl>
                            <div className="space-y-2">
                              {watchedMunicipalities.map(
                                (municipality, index) => (
                                  <div
                                    key={index}
                                    draggable
                                    onDragStart={() =>
                                      handleDragStart(index, 'municipalities')
                                    }
                                    onDragOver={handleDragOver}
                                    onDrop={(e) =>
                                      handleDrop(e, index, 'municipalities')
                                    }
                                    className={`flex items-center gap-3 p-3 bg-muted rounded-xl border cursor-move hover:bg-muted transition-colors ${draggedIndex === index &&
                                        draggedType === 'municipalities'
                                        ? 'opacity-50'
                                        : ''
                                      }`}
                                  >
                                    <GripVertical className="w-4 h-4 text-muted-foreground/70" />
                                    <span className="text-sm font-medium text-foreground flex-1">
                                      {municipality}
                                    </span>
                                    <Button type="button"
                                      onClick={() => removeMunicipality(index)}
                                      className="p-1 hover:bg-muted rounded text-red-500"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Add New Municipality */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add new Municipality"
                        value={newMunicipality}
                        onChange={(e) => setNewMunicipality(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === 'Enter' &&
                          (e.preventDefault(), addMunicipality())
                        }
                      />
                      <Button
                        type="button"
                        onClick={addMunicipality}
                        size="sm"
                        className="bg-foreground-soft hover:bg-foreground"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Bottom Row - Proposal Notifications (Full Width) */}
              <Card className="border border-border">
                <CardHeader>
                  <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground">
                    Proposal Notifications
                  </h3>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Proposal Modified Notifications */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-md font-bold tracking-tight text-foreground">
                          Proposal Modified Notifications
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Who gets notified when a proposal is modified?
                        </p>
                      </div>

                      {/* Modified Emails List */}
                      <div className="space-y-2">
                        {watchedProposalModifiedEmails.map((email, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-muted rounded-xl border"
                          >
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium text-foreground">
                                {email}
                              </span>
                            </div>
                            <Button type="button"
                              onClick={() => removeProposalModifiedEmail(index)}
                              className="p-1 hover:bg-muted rounded text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      {/* Add New Modified Email */}
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            placeholder="Enter email address"
                            value={newProposalModifiedEmail}
                            onChange={(e) =>
                              handleEmailChange(
                                e.target.value,
                                'proposalModified'
                              )
                            }
                            onKeyPress={(e) =>
                              e.key === 'Enter' &&
                              (e.preventDefault(), addProposalModifiedEmail())
                            }
                            className={
                              emailErrors.proposalModified
                                ? 'border-red-500'
                                : ''
                            }
                          />
                          <Button
                            type="button"
                            onClick={addProposalModifiedEmail}
                            size="sm"
                            className="bg-foreground-soft hover:bg-foreground"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        {emailErrors.proposalModified && (
                          <p className="text-sm text-red-500">
                            {emailErrors.proposalModified}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Proposal Sold Notifications */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-md font-bold tracking-tight text-foreground">
                          Proposal Sold Notifications
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Who gets notified when a proposal is sold?
                        </p>
                      </div>

                      {/* Sold Emails List */}
                      <div className="space-y-2">
                        {watchedProposalSoldEmails.map((email, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-muted rounded-xl border"
                          >
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium text-foreground">
                                {email}
                              </span>
                            </div>
                            <Button type="button"
                              onClick={() => removeProposalSoldEmail(index)}
                              className="p-1 hover:bg-muted rounded text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      {/* Add New Sold Email */}
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            placeholder="Enter email address"
                            value={newProposalSoldEmail}
                            onChange={(e) =>
                              handleEmailChange(e.target.value, 'proposalSold')
                            }
                            onKeyPress={(e) =>
                              e.key === 'Enter' &&
                              (e.preventDefault(), addProposalSoldEmail())
                            }
                            className={
                              emailErrors.proposalSold ? 'border-red-500' : ''
                            }
                          />
                          <Button
                            type="button"
                            onClick={addProposalSoldEmail}
                            size="sm"
                            className="bg-foreground-soft hover:bg-foreground"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        {emailErrors.proposalSold && (
                          <p className="text-sm text-red-500">
                            {emailErrors.proposalSold}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Website Lead Forms (Optional) */}
              <Card className="border border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-muted-foreground" />
                      <h3 className="font-newsreader text-xl font-semibold tracking-tight text-foreground">
                        Website Lead Forms
                      </h3>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">Optional</span>
                    </div>
                    <Switch
                      checked={enableWebhook}
                      onCheckedChange={setEnableWebhook}
                    />
                  </div>
                  <CardDescription>
                    Connect your WordPress contact form so submissions become leads in LarkPM automatically.
                    You can skip this now and set it up later under Settings → Website Lead Integration.
                  </CardDescription>
                </CardHeader>
                {enableWebhook && (
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Form Platform</Label>
                      <Select value={webhookPlatform} onValueChange={setWebhookPlatform}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your form plugin" />
                        </SelectTrigger>
                        <SelectContent>
                          {WEBHOOK_PLATFORMS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {(webhookConfig || webhookCreated) && (
                      <div className="flex items-center gap-2 text-sm text-success bg-success/10 p-3 rounded-xl border border-success/20">
                        <Check className="h-4 w-4 flex-shrink-0" />
                        Webhook URL generated for your company. Continue with Phase 2 in the guide below to paste it into WordPress.
                      </div>
                    )}

                    <WebhookConnectionGuide
                      variant="onboarding"
                      platform={webhookPlatform}
                      urlGenerated={Boolean(webhookConfig || webhookCreated)}
                      webhookUrl={copyableOnboardingUrl ?? undefined}
                      onCopyUrl={
                        copyableOnboardingUrl
                          ? () => copyWebhookUrl(copyableOnboardingUrl)
                          : undefined
                      }
                      generateAction={
                        !(webhookConfig || webhookCreated) ? (
                          <Button
                            type="button"
                            onClick={handleCreateWebhook}
                            disabled={createWebhookConfig.isPending}
                            className="bg-foreground-soft hover:bg-foreground"
                          >
                            {createWebhookConfig.isPending ? 'Setting up...' : 'Generate Webhook URL'}
                          </Button>
                        ) : undefined
                      }
                    />
                  </CardContent>
                )}
              </Card>
            </div>
          </Form>
        )}
      </div>
    </OnboardingLayout>
  );
}
